import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type { TransactionsQuery } from "./client";
import { walletApi } from "./client";

export const walletKeys = {
  all: ["wallet"] as const,
  overview: () => ["wallet", "overview"] as const,
  accountsStats: () => ["wallet", "accounts", "stats"] as const,
  accounts: (q: Partial<OffsetPageQueryDTO>) =>
    ["wallet", "accounts", q] as const,
  transactions: (q: Partial<TransactionsQuery>) =>
    ["wallet", "transactions", q] as const,
};

export function useWalletOverviewQuery() {
  return useQuery({
    queryKey: walletKeys.overview(),
    queryFn: () => walletApi.getOverview(),
  });
}

export function useWalletAccountsStatsQuery() {
  return useQuery({
    queryKey: walletKeys.accountsStats(),
    queryFn: () => walletApi.getAccountsStats(),
  });
}

export function useWalletAccountsQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: walletKeys.accounts(q),
    queryFn: () => walletApi.listAccounts(q),
  });
}

export function useWalletTransactionsQuery(
  query: Partial<TransactionsQuery> = {},
) {
  const q: TransactionsQuery = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    walletId: query.walletId,
    search: query.search,
  };
  return useQuery({
    queryKey: walletKeys.transactions(q),
    queryFn: () => walletApi.listTransactions(q),
  });
}

export function useUpdateKycTierMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      walletId,
      kycTier,
    }: {
      walletId: string;
      kycTier: string;
    }) => walletApi.updateKycTier(walletId, kycTier),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walletKeys.all });
      toast.success("ອັບເດດ KYC Tier ສຳເລັດ");
    },
    onError: () => {
      toast.error("ອັບເດດ KYC Tier ລົ້ມເຫຼວ");
    },
  });
}
